import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { contactsAPI } from '../services/api';
import NavigationBar from './NavigationBar';
import Snackbar from './Snackbar';
import LoadingSpinner from './LoadingSpinner';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { Country, State } from 'country-state-city';
import './EditUserProfile.css';
import { eventHomePath } from '../config/events';

// Dietary Restrictions options
const DIETARY_RESTRICTIONS = [
    'None',
    'Dairy',
    'Gluten Allergy',
    'Halal',
    'Kosher',
    'Nut Allergy',
    'Pescatarian',
    'Shellfish Allergy',
    'Vegan',
    'Vegetarian'
];

// Timezone list
const TIMEZONES = [
    { value: 'America/New_York', label: 'Eastern Time (New York)' },
    { value: 'America/Chicago', label: 'Central Time (Chicago)' },
    { value: 'America/Denver', label: 'Mountain Time (Denver)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)' },
    { value: 'America/Phoenix', label: 'Arizona Time (Phoenix)' },
    { value: 'America/Anchorage', label: 'Alaska Time (Anchorage)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (Honolulu)' },
    { value: 'America/Toronto', label: 'Eastern Time (Toronto)' },
    { value: 'America/Vancouver', label: 'Pacific Time (Vancouver)' },
    { value: 'America/Mexico_City', label: 'Central Time (Mexico City)' },
    { value: 'America/Sao_Paulo', label: 'Brasilia Time (São Paulo)' },
    { value: 'America/Buenos_Aires', label: 'Argentina Time (Buenos Aires)' },
    { value: 'Europe/London', label: 'Greenwich Mean Time (London)' },
    { value: 'Europe/Paris', label: 'Central European Time (Paris)' },
    { value: 'Europe/Berlin', label: 'Central European Time (Berlin)' },
    { value: 'Europe/Rome', label: 'Central European Time (Rome)' },
    { value: 'Europe/Madrid', label: 'Central European Time (Madrid)' },
    { value: 'Europe/Amsterdam', label: 'Central European Time (Amsterdam)' },
    { value: 'Europe/Stockholm', label: 'Central European Time (Stockholm)' },
    { value: 'Europe/Moscow', label: 'Moscow Time (Moscow)' },
    { value: 'Asia/Tokyo', label: 'Japan Standard Time (Tokyo)' },
    { value: 'Asia/Shanghai', label: 'China Standard Time (Shanghai)' },
    { value: 'Asia/Hong_Kong', label: 'Hong Kong Time (Hong Kong)' },
    { value: 'Asia/Singapore', label: 'Singapore Time (Singapore)' },
    { value: 'Asia/Seoul', label: 'Korea Standard Time (Seoul)' },
    { value: 'Asia/Mumbai', label: 'India Standard Time (Mumbai)' },
    { value: 'Asia/Dubai', label: 'Gulf Standard Time (Dubai)' },
    { value: 'Asia/Bangkok', label: 'Indochina Time (Bangkok)' },
    { value: 'Asia/Jakarta', label: 'Western Indonesia Time (Jakarta)' },
    { value: 'Australia/Sydney', label: 'Australian Eastern Time (Sydney)' },
    { value: 'Australia/Melbourne', label: 'Australian Eastern Time (Melbourne)' },
    { value: 'Australia/Perth', label: 'Australian Western Time (Perth)' },
    { value: 'Pacific/Auckland', label: 'New Zealand Time (Auckland)' },
    { value: 'UTC', label: 'Coordinated Universal Time (UTC)' }
];

const EditUserProfile = () => {
    const { contactId } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ isOpen: false, message: '', type: 'success' });
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [isDietaryDropdownOpen, setIsDietaryDropdownOpen] = useState(false);
    const dietaryDropdownRef = useRef(null);
    
    // Track where user came from
    const [returnUrl, setReturnUrl] = useState(`/user/${contactId}`);

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        job_title: '',
        mobile_phone: '',
        whatsapp_number: '',
        office_number: '',
        assistant_first_name: '',
        assistant_last_name: '',
        assistant_email: '',
        timezone: 'America/New_York',
        linkedin_url: '',
        instagram: '',
        country: '',
        state_province: '',
        city: '',
        postal_code: '',
        address_line1: '',
        address_line2: '',
        short_bio: '',
        image_url: '',
        dietary_restrictions: []
    });

    // Check if user is editing their own profile
    const isOwnProfile = user?.contact_id === parseInt(contactId);

    useEffect(() => {
        // Check if user came from home page via URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const fromHome = urlParams.get('from') === 'home';
        
        if (fromHome) {
            setReturnUrl(eventHomePath());
        } else {
            setReturnUrl(`/user/${contactId}`);
        }
    }, [contactId]);

    useEffect(() => {
        // Redirect if not own profile
        if (!loading && !isOwnProfile) {
            navigate(`/user/${contactId}`);
            return;
        }
    }, [isOwnProfile, loading, contactId, navigate]);

    useEffect(() => {
        // Load countries on mount
        const allCountries = Country.getAllCountries();
        setCountries(allCountries);
    }, []);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                setLoading(true);
                // Use secure /me endpoint for authenticated user
                const response = await contactsAPI.getMyProfile();
                setProfile(response.data);

                // Populate form
                setFormData({
                    first_name: response.data.first_name || '',
                    last_name: response.data.last_name || '',
                    job_title: response.data.job_title || '',
                    mobile_phone: response.data.mobile_phone || '',
                    whatsapp_number: response.data.whatsapp_number || '',
                    office_number: response.data.office_number || '',
                    assistant_first_name: response.data.assistant_first_name || '',
                    assistant_last_name: response.data.assistant_last_name || '',
                    assistant_email: response.data.assistant_email || '',
                    timezone: response.data.timezone || 'America/New_York',
                    linkedin_url: response.data.linkedin_url || '',
                    instagram: response.data.instagram || '',
                    country: response.data.country || '',
                    state_province: response.data.state_province || '',
                    city: response.data.city || '',
                    postal_code: response.data.postal_code || '',
                    address_line1: response.data.address_line1 || '',
                    address_line2: response.data.address_line2 || '',
                    short_bio: response.data.short_bio || '',
                    image_url: response.data.image_url || '',
                    dietary_restrictions: Array.isArray(response.data.dietary_restrictions)
                        ? response.data.dietary_restrictions
                        : []
                });

                // Load states if country is selected
                if (response.data.country) {
                    const countryData = Country.getAllCountries().find(c => c.name === response.data.country);
                    if (countryData) {
                        const countryStates = State.getStatesOfCountry(countryData.isoCode);
                        setStates(countryStates);
                    }
                }

                setError(null);
            } catch (err) {
                console.error('Error fetching profile:', err);
                setError('Failed to load profile');
            } finally {
                setLoading(false);
            }
        };

        if (isOwnProfile) {
            fetchProfile();
        }
    }, [isOwnProfile]);

    // Close dietary dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dietaryDropdownRef.current && !dietaryDropdownRef.current.contains(event.target)) {
                setIsDietaryDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // If country changes, load states
        if (name === 'country') {
            const countryData = countries.find(c => c.name === value);
            if (countryData) {
                const countryStates = State.getStatesOfCountry(countryData.isoCode);
                setStates(countryStates);
                // Clear state_province if country changes
                setFormData(prev => ({ ...prev, state_province: '' }));
            } else {
                setStates([]);
            }
        }
    };

    const handlePhoneChange = (phone) => {
        setFormData(prev => ({ ...prev, mobile_phone: phone ? `+${phone}` : '' }));
    };

    const handleWhatsAppChange = (phone) => {
        setFormData(prev => ({ ...prev, whatsapp_number: phone ? `+${phone}` : '' }));
    };

    const handleOfficeNumberChange = (phone) => {
        setFormData(prev => ({ ...prev, office_number: phone ? `+${phone}` : '' }));
    };

    const handleDietaryRestrictionToggle = (restriction) => {
        setFormData(prev => {
            const current = prev.dietary_restrictions || [];

            // If "None" is selected, clear all others and only keep "None"
            if (restriction === 'None') {
                return { ...prev, dietary_restrictions: ['None'] };
            }

            // If another option is selected, remove "None"
            let updated = current.filter(r => r !== 'None');

            // Toggle the selected restriction
            if (updated.includes(restriction)) {
                updated = updated.filter(r => r !== restriction);
            } else {
                updated = [...updated, restriction];
            }

            // If nothing is selected, default to empty array
            return { ...prev, dietary_restrictions: updated.length > 0 ? updated : [] };
        });
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Please select an image file');
            return;
        }

        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
            setError('Image must be less than 5MB');
            return;
        }

        try {
            setSaving(true);
            setError(null);

            // Upload to S3 (backend now auto-saves to database)
            const response = await contactsAPI.uploadMyImage(file);
            const imageUrl = response.data.image_url;

            // Update form data with S3 URL
            setFormData(prev => ({ ...prev, image_url: imageUrl }));

            // Dispatch custom event to notify other components (NavigationBar, UserProfile)
            window.dispatchEvent(new CustomEvent('profileImageUpdated', { detail: { imageUrl } }));

            setSnackbar({ isOpen: true, message: 'Image uploaded successfully!', type: 'success' });
        } catch (err) {
            console.error('Error uploading image:', err);
            setError(err.response?.data?.detail || 'Failed to upload image');
        } finally {
            setSaving(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.first_name || !formData.last_name || !formData.job_title ||
            !formData.mobile_phone || !formData.timezone || !formData.country || !formData.city) {
            setError('Please fill in all required fields');
            return;
        }

        try {
            setSaving(true);
            setError(null);

            // Set is_onboarded to true when all required fields are filled
            const updatedData = {
                ...formData,
                is_onboarded: true
            };

            await contactsAPI.updateMyProfile(updatedData);

            setSnackbar({ isOpen: true, message: 'Your Profile Has Been Saved!', type: 'success' });
            
            // Dispatch event to update profile view if needed
            window.dispatchEvent(new CustomEvent('profileUpdated'));
            
            // Navigate back after short delay
            setTimeout(() => {
                navigate(returnUrl);
            }, 1500);
        } catch (err) {
            console.error('Error updating profile:', err);
            setError(err.response?.data?.detail || 'Failed to update profile');
            setSnackbar({ isOpen: true, message: 'Failed to update profile', type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        navigate(returnUrl);
    };

    if (loading) {
        return (
            <>
                <NavigationBar />
                <div className="edit-profile-container">
                    <LoadingSpinner message="Loading profile..." />
                </div>
            </>
        );
    }

    if (error && !profile) {
        return (
            <>
                <NavigationBar />
                <div className="edit-profile-container">
                    <div className="edit-profile-error">
                        <h2>Error</h2>
                        <p>{error}</p>
                        <button onClick={handleCancel} className="btn-secondary">
                            Go Back
                        </button>
                    </div>
                </div>
            </>
        );
    }

    if (!isOwnProfile) {
        return null; // Will redirect in useEffect
    }

    return (
        <>
            <NavigationBar />
            <div className="edit-profile-container">
                <div className="edit-profile-header">
                    <p>Update your personal information</p>
                </div>

                {error && (
                    <div className="error-message">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="edit-profile-form">
                    {/* Email - Read Only */}
                    <div className="form-section">
                        <h2>Account Information</h2>
                        <div className="form-group">
                            <label>Email Address</label>
                            <input
                                type="email"
                                value={profile?.email || ''}
                                disabled
                                className="input-disabled"
                            />
                            <span className="form-helper">Email cannot be changed. Contact support if needed.</span>
                        </div>
                    </div>

                    {/* Profile Photo */}
                    <div className="form-section">
                        <h2>Badge Photo</h2>
                        <div className="form-group">
                            <div className="photo-upload">
                                <div className="photo-preview">
                                    {formData.image_url ? (
                                        <img src={formData.image_url} alt="Profile" />
                                    ) : (
                                        <div className="photo-placeholder">
                                            {profile?.first_name?.charAt(0)}{profile?.last_name?.charAt(0)}
                                        </div>
                                    )}
                                </div>
                                <div className="photo-actions">
                                    <label htmlFor="photo-upload" className="btn-secondary">
                                        Upload Photo
                                    </label>
                                    <input
                                        id="photo-upload"
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        style={{ display: 'none' }}
                                    />
                                    <span className="form-helper">JPG, PNG or GIF. Max 5MB.</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Personal Information */}
                    <div className="form-section">
                        <h2>Personal Information</h2>
                        <div className="form-row">
                            <div className="form-group">
                                <label>First Name <span className="required">*</span></label>
                                <input
                                    type="text"
                                    name="first_name"
                                    value={formData.first_name}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Last Name <span className="required">*</span></label>
                                <input
                                    type="text"
                                    name="last_name"
                                    value={formData.last_name}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Job Title <span className="required">*</span></label>
                            <input
                                type="text"
                                name="job_title"
                                value={formData.job_title}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Short Contact Bio (250 character limit)</label>
                            <textarea
                                name="short_bio"
                                className="form-input"
                                placeholder="Brief professional bio or description..."
                                value={formData.short_bio}
                                onChange={handleChange}
                                maxLength={250}
                                rows={4}
                                style={{ resize: 'vertical', minHeight: '100px' }}
                            />
                            <div className="character-count">
                                {formData.short_bio.length}/250 characters
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Mobile Phone <span className="required">*</span></label>
                            <PhoneInput
                                country={'us'}
                                value={formData.mobile_phone ? String(formData.mobile_phone).replace(/^\+/, '') : ''}
                                onChange={handlePhoneChange}
                                containerStyle={{ width: '100%' }}
                                inputStyle={{
                                    width: '100%',
                                    height: '48px',
                                    fontSize: '16px',
                                }}
                                buttonStyle={{ height: '48px' }}
                                enableSearch={true}
                                searchPlaceholder="Search countries..."
                                preferredCountries={['us', 'gb', 'ca']}
                                countryCodeEditable={false}
                                disableDropdown={false}
                            />
                        </div>

                        <div className="form-group">
                            <label>WhatsApp Number</label>
                            <PhoneInput
                                country={'us'}
                                value={formData.whatsapp_number ? String(formData.whatsapp_number).replace(/^\+/, '') : ''}
                                onChange={handleWhatsAppChange}
                                containerStyle={{ width: '100%' }}
                                inputStyle={{
                                    width: '100%',
                                    height: '48px',
                                    fontSize: '16px',
                                }}
                                buttonStyle={{ height: '48px' }}
                                enableSearch={true}
                                searchPlaceholder="Search countries..."
                                preferredCountries={['us', 'gb', 'ca']}
                                countryCodeEditable={false}
                                disableDropdown={false}
                            />
                        </div>

                        <div className="form-group">
                            <label>Office Number</label>
                            <PhoneInput
                                country={'us'}
                                value={formData.office_number ? String(formData.office_number).replace(/^\+/, '') : ''}
                                onChange={handleOfficeNumberChange}
                                containerStyle={{ width: '100%' }}
                                inputStyle={{
                                    width: '100%',
                                    height: '48px',
                                    fontSize: '16px',
                                }}
                                buttonStyle={{ height: '48px' }}
                                enableSearch={true}
                                searchPlaceholder="Search countries..."
                                preferredCountries={['us', 'gb', 'ca']}
                                countryCodeEditable={false}
                                disableDropdown={false}
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Assistant First Name</label>
                                <input
                                    type="text"
                                    name="assistant_first_name"
                                    className="form-input"
                                    placeholder="Assistant's first name"
                                    value={formData.assistant_first_name}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="form-group">
                                <label>Assistant Last Name</label>
                                <input
                                    type="text"
                                    name="assistant_last_name"
                                    className="form-input"
                                    placeholder="Assistant's last name"
                                    value={formData.assistant_last_name}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Assistant Email</label>
                            <input
                                type="email"
                                name="assistant_email"
                                className="form-input"
                                placeholder="assistant@example.com"
                                value={formData.assistant_email}
                                onChange={handleChange}
                            />
                            <small className="form-help-text">Optional: Email address of your assistant for notifications</small>
                        </div>

                        <div className="form-group">
                            <label>Time Zone <span className="required">*</span></label>
                            <select
                                name="timezone"
                                value={formData.timezone}
                                onChange={handleChange}
                                required
                            >
                                {TIMEZONES.map((tz) => (
                                    <option key={tz.value} value={tz.value}>
                                        {tz.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Dietary Restrictions */}
                    <div className="form-section">
                        <h2>Dietary Restrictions</h2>
                        <div className="form-group">
                            <label>Select all that apply</label>
                            <div className="custom-dropdown" ref={dietaryDropdownRef}>
                                <div
                                    className="dropdown-trigger"
                                    onClick={() => setIsDietaryDropdownOpen(!isDietaryDropdownOpen)}
                                >
                                    <span className="dropdown-text">
                                        {formData.dietary_restrictions?.length > 0
                                            ? formData.dietary_restrictions.join(', ')
                                            : 'Select dietary restrictions...'}
                                    </span>
                                    <svg
                                        className={`dropdown-arrow ${isDietaryDropdownOpen ? 'open' : ''}`}
                                        width="12"
                                        height="8"
                                        viewBox="0 0 12 8"
                                        fill="none"
                                    >
                                        <path
                                            d="M1 1L6 6L11 1"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </div>
                                {isDietaryDropdownOpen && (
                                    <div className="dropdown-menu">
                                        {DIETARY_RESTRICTIONS.map((restriction) => (
                                            <label key={restriction} className="dropdown-checkbox-item">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.dietary_restrictions?.includes(restriction) || false}
                                                    onChange={() => handleDietaryRestrictionToggle(restriction)}
                                                />
                                                <span>{restriction}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <small className="form-help-text">
                                Select one or more options. Selecting "None" will clear all other selections.
                            </small>
                        </div>
                    </div>

                    {/* Social Links */}
                    <div className="form-section">
                        <h2>Social Links</h2>
                        <div className="form-group">
                            <label>LinkedIn URL</label>
                            <input
                                type="url"
                                name="linkedin_url"
                                value={formData.linkedin_url}
                                onChange={handleChange}
                                placeholder="https://linkedin.com/in/yourprofile"
                            />
                        </div>
                        <div className="form-group">
                            <label>Instagram</label>
                            <input
                                type="text"
                                name="instagram"
                                value={formData.instagram}
                                onChange={handleChange}
                                placeholder="@username or full URL"
                            />
                        </div>
                    </div>

                    {/* Address */}
                    <div className="form-section">
                        <h2>Address</h2>
                        <div className="form-group">
                            <label>Country <span className="required">*</span></label>
                            <select
                                name="country"
                                value={formData.country}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select a country</option>
                                {countries.map((country) => (
                                    <option key={country.isoCode} value={country.name}>
                                        {country.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>State/Province</label>
                                <select
                                    name="state_province"
                                    value={formData.state_province}
                                    onChange={handleChange}
                                    disabled={!formData.country || states.length === 0}
                                >
                                    <option value="">Select a state/province</option>
                                    {states.map((state) => (
                                        <option key={state.isoCode} value={state.name}>
                                            {state.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>City <span className="required">*</span></label>
                                <input
                                    type="text"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Postal Code</label>
                            <input
                                type="text"
                                name="postal_code"
                                value={formData.postal_code}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="form-group">
                            <label>Address Line 1</label>
                            <input
                                type="text"
                                name="address_line1"
                                value={formData.address_line1}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="form-group">
                            <label>Address Line 2</label>
                            <input
                                type="text"
                                name="address_line2"
                                value={formData.address_line2}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className="form-actions">
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="btn-secondary"
                            disabled={saving}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn-primary"
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>

            {/* Snackbar Notification */}
            <Snackbar
                message={snackbar.message}
                type={snackbar.type}
                isOpen={snackbar.isOpen}
                onClose={() => setSnackbar({ ...snackbar, isOpen: false })}
                duration={3000}
            />
        </>
    );
};

export default EditUserProfile;

